import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useClient } from '../context/ClientContext';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import MovementsBridge, { BoundariesPanel } from '../components/MovementsBridge';
import {
  Sparkles, Download, Loader2, RefreshCw, CalendarDays, X,
  ChevronRight, TrendingUp, ShieldAlert, AlertTriangle, CheckCircle2, Wand2,
} from 'lucide-react';

const GRANULARITY_OPTIONS = [
  { value: 'month', label: 'Monthly' },
  { value: 'week', label: 'Weekly' },
  { value: 'rolling_30d', label: 'Rolling 30-day' },
];

const todayISO = () => new Date().toISOString().slice(0, 10);
const monthsAgoISO = (m) => {
  const d = new Date(); d.setMonth(d.getMonth() - m);
  return d.toISOString().slice(0, 10);
};

const RULE_DOMAIN = {
  agg_settlement_shortfall_v1: 'Aggregator settlements',
  sup_rate_over_contract_v1: 'Supplier contracts',
  sup_recurring_variance_v1: 'Supplier price creep',
  recon_card_vs_bill_gap_v1: 'Bank reconciliation',
  pnl_food_cost_pct_shift_v1: 'P&L movements',
};

const SEVERITY_STYLE = {
  high: { pill: 'bg-rose-100 text-rose-800 border-rose-300', icon: ShieldAlert, dot: 'bg-rose-500' },
  med:  { pill: 'bg-amber-100 text-amber-800 border-amber-300', icon: AlertTriangle, dot: 'bg-amber-500' },
  low:  { pill: 'bg-slate-100 text-slate-700 border-slate-300', icon: CheckCircle2, dot: 'bg-slate-400' },
};

const fmtINR = (n) => {
  if (n == null) return '—';
  try { return '₹' + Number(n).toLocaleString('en-IN', { maximumFractionDigits: 2 }); }
  catch { return '—'; }
};

export const Movements = () => {
  const { selectedClient, API } = useClient();
  const { user } = useAuth();
  const isClient = user?.role === 'client';
  const [granularity, setGranularity] = useState('month');
  const [mode, setMode] = useState('tile');   // 'tile' | 'custom'
  const [periods, setPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [customStart, setCustomStart] = useState(monthsAgoISO(6));
  const [customEnd, setCustomEnd] = useState(todayISO());
  const [data, setData] = useState({ count: 0, total_recoverable_inr: 0, negotiation_headroom_inr: 0, by_severity: {}, findings: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runSummary, setRunSummary] = useState(null);
  const [polishing, setPolishing] = useState(false);
  const [polishSummary, setPolishSummary] = useState(null);
  const [drawer, setDrawer] = useState(null);

  // Effective (start, end) driving the finding query, based on current mode
  const effectiveStart = mode === 'custom' ? customStart : selectedPeriod?.period_start;
  const effectiveEnd = mode === 'custom' ? customEnd : selectedPeriod?.period_end;
  const effectiveQueryMode = mode === 'custom' ? 'overlap' : 'exact';

  // Load available periods whenever client or granularity changes
  const loadPeriods = useCallback(async () => {
    if (!selectedClient) return;
    setError('');
    try {
      const { data } = await axios.get(
        `${API}/variance-findings/${selectedClient.id}/available-periods`,
        { params: { granularity } },
      );
      setPeriods(data.periods || []);
      // Auto-select newest period
      if ((data.periods || []).length > 0) {
        setSelectedPeriod(data.periods[0]);
      } else {
        setSelectedPeriod(null);
        setData({ count: 0, total_recoverable_inr: 0, negotiation_headroom_inr: 0, by_severity: {}, findings: [] });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load periods');
    }
  }, [selectedClient, API, granularity]);

  useEffect(() => { loadPeriods(); }, [loadPeriods]);

  const load = useCallback(async () => {
    if (!selectedClient || !effectiveStart || !effectiveEnd) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(
        `${API}/variance-findings/${selectedClient.id}`,
        { params: {
          start: effectiveStart,
          end: effectiveEnd,
          granularity: mode === 'custom' ? 'any' : granularity,
          mode: effectiveQueryMode,
        }},
      );
      setData(data);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to load findings');
      setData({ count: 0, total_recoverable_inr: 0, negotiation_headroom_inr: 0, by_severity: {}, findings: [] });
    } finally { setLoading(false); }
  }, [selectedClient, API, effectiveStart, effectiveEnd, granularity, mode, effectiveQueryMode]);

  useEffect(() => { load(); }, [load]);

  const downloadBrief = async () => {
    if (!selectedClient || !effectiveStart || !effectiveEnd) return;
    setDownloading(true);
    try {
      const res = await fetch(
        `${API}/reports/movements-brief/${selectedClient.id}?start=${effectiveStart}&end=${effectiveEnd}&granularity=${mode === 'custom' ? 'any' : granularity}&mode=${effectiveQueryMode}`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = `movements_brief_${effectiveStart}_${effectiveEnd}.pdf`;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(`Download failed: ${e.message}`);
    } finally { setDownloading(false); }
  };

  // Consolidated counterparty-facing dispute brief for the CURRENT view.
  // WYSIWYG: uses the same period + granularity + mode as the list on-screen.
  const [downloadingDispute, setDownloadingDispute] = useState(false);
  const downloadDisputeBrief = async () => {
    if (!selectedClient || !effectiveStart || !effectiveEnd) return;
    setDownloadingDispute(true);
    try {
      const url = `${API}/variance-findings/${selectedClient.id}/dispute-brief.pdf`
        + `?start=${effectiveStart}&end=${effectiveEnd}`
        + `&granularity=${mode === 'custom' ? 'any' : granularity}`
        + `&mode=${effectiveQueryMode}`;
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
      const blob = await res.blob();
      const disp = res.headers.get('content-disposition') || '';
      const m = disp.match(/filename="?([^";]+)"?/i);
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = (m && m[1]) || `dispute_brief_${effectiveStart}_${effectiveEnd}.pdf`;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(`Dispute brief download failed: ${e.message}`);
    } finally { setDownloadingDispute(false); }
  };


  const runEngine = async () => {
    setRunning(true); setError(''); setRunSummary(null);
    try {
      const { data } = await axios.post(
        `${API}/admin/scheduler/run-variance-engine-now`,
        null,
        { params: { months: 12, weeks: 26, rolling_days: 30 } },
      );
      setRunSummary(data.summary);
      await loadPeriods();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Engine run failed');
    } finally { setRunning(false); }
  };

  const polishNarratives = async () => {
    if (!selectedClient || !effectiveStart || !effectiveEnd) return;
    setPolishing(true); setError(''); setPolishSummary(null);
    try {
      const { data } = await axios.post(
        `${API}/variance-findings/${selectedClient.id}/polish`,
        null,
        { params: {
          start: effectiveStart, end: effectiveEnd,
          granularity: mode === 'custom' ? 'any' : granularity,
          mode: effectiveQueryMode,
          limit: 25,
        }},
      );
      setPolishSummary(data);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Polish failed');
    } finally { setPolishing(false); }
  };


  // Download the counterparty-facing Evidence Bundle PDF for a single finding.
  // Uses fetch with credentials so the browser sends the auth cookie AND we
  // can force a download attribute on the anchor for a friendly filename.
  const [downloadingBundleId, setDownloadingBundleId] = useState(null);
  const downloadEvidenceBundle = async (finding, event) => {
    if (event) { event.stopPropagation(); event.preventDefault(); }
    if (!selectedClient || !finding?.id) return;
    setDownloadingBundleId(finding.id);
    try {
      const res = await fetch(
        `${API}/variance-findings/${selectedClient.id}/${finding.id}/evidence-bundle.pdf`,
        { credentials: 'include' },
      );
      if (!res.ok) throw new Error(`${res.status} ${(await res.text()).slice(0, 200)}`);
      const blob = await res.blob();
      const disp = res.headers.get('content-disposition') || '';
      const nameMatch = disp.match(/filename="?([^";]+)"?/i);
      const filename = (nameMatch && nameMatch[1]) || `evidence-${finding.id.slice(0, 8)}.pdf`;
      const a = document.createElement('a');
      a.href = window.URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      window.URL.revokeObjectURL(a.href);
    } catch (e) {
      setError(`Evidence bundle download failed: ${e.message}`);
    } finally {
      setDownloadingBundleId(null);
    }
  };



  const openDrawer = async (finding) => {
    setDrawer({ finding, resolvedCitations: null, error: null });
    // Resolve up to 6 citations for a quick auditor preview
    try {
      const heads = (finding.citations || []).slice(0, 6);
      const resolved = await Promise.all(
        heads.map(async (c) => {
          try {
            const { data } = await axios.get(
              `${API}/variance-findings/citation/${c.collection}/${c.id}`,
              { params: { client_id: selectedClient.id } },
            );
            return { ok: true, collection: c.collection, id: c.id, key_fields: c.key_fields, row: data.row };
          } catch (e) {
            return { ok: false, collection: c.collection, id: c.id, key_fields: c.key_fields,
                     error: e?.response?.data?.detail || 'resolve failed' };
          }
        }),
      );
      setDrawer((d) => d && d.finding.id === finding.id
        ? { ...d, resolvedCitations: resolved } : d);
    } catch (e) {
      setDrawer((d) => d && d.finding.id === finding.id
        ? { ...d, error: e.message } : d);
    }
  };

  if (!selectedClient) {
    return <div className="text-center text-text-secondary py-12" data-testid="movements-no-client">
      Select a client to view movements.
    </div>;
  }

  return (
    <div className="space-y-6" data-testid="movements-page">
      <header>
        <p className="text-xs uppercase tracking-[0.2em] text-text-secondary font-semibold mb-1">
          Deterministic Variance Engine · Movements
        </p>
        <h1 className="text-3xl font-bold text-primary">Movements — {selectedClient.name}</h1>
        <p className="text-sm text-text-secondary mt-1">
          Findings tiled by monthly, weekly, and rolling-30-day windows.
          Nightly at 03:15 IST · use <em>Run engine now</em> for on-demand refresh.
        </p>
      </header>

      {/* Iter 114 · Gate 2 — Deterministic movement bridge (revenue,
          channel, cost, margin) computed from the same P&L source
          the Monthly Report uses. No LLM here; narrative is Gate 3. */}
      <MovementsBridge
        clientId={selectedClient.id}
        API={API}
        start={effectiveStart}
        end={effectiveEnd}
      />

      {/* Mode toggle + granularity + period picker (dual mode) */}
      <div className="bg-white border border-border rounded-md p-4 space-y-3" data-testid="movements-filters">
        {/* Row 1: mode toggle */}
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">Mode</span>
          <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="movements-mode-toggle">
            <button onClick={() => setMode('tile')}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      mode === 'tile' ? 'bg-primary text-white'
                        : 'bg-white text-text-secondary hover:bg-background-secondary'
                    }`}
                    data-testid="mode-tile">
              Tiles (curated)
            </button>
            <button onClick={() => setMode('custom')}
                    className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                      mode === 'custom' ? 'bg-primary text-white'
                        : 'bg-white text-text-secondary hover:bg-background-secondary'
                    }`}
                    data-testid="mode-custom">
              Custom range
            </button>
          </div>
          <span className="text-[11px] text-text-muted">
            {mode === 'tile'
              ? 'Only date ranges the engine actually computed — dates are guaranteed correct.'
              : 'Pick any range; overlapping tiles are returned. Broader ranges include more findings.'}
          </span>
        </div>

        {/* Row 2: pickers */}
        <div className="flex flex-wrap items-end gap-3">
          {mode === 'tile' && (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">
                  <CalendarDays className="inline h-3 w-3 mr-1" /> Granularity
                </label>
                <div className="inline-flex rounded-md border border-border overflow-hidden" data-testid="movements-granularity-toggle">
                  {GRANULARITY_OPTIONS.map(opt => (
                    <button key={opt.value}
                            onClick={() => setGranularity(opt.value)}
                            className={`px-3 py-1.5 text-xs font-semibold transition-colors ${
                              granularity === opt.value ? 'bg-primary text-white'
                                : 'bg-white text-text-secondary hover:bg-background-secondary'
                            }`}
                            data-testid={`granularity-${opt.value}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="min-w-[300px] flex-1">
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">
                  Period ({periods.length} available with variance)
                </label>
                <select
                  value={selectedPeriod ? `${selectedPeriod.period_start}|${selectedPeriod.period_end}` : ''}
                  onChange={(e) => {
                    const [ps, pe] = e.target.value.split('|');
                    const p = periods.find(x => x.period_start === ps && x.period_end === pe);
                    setSelectedPeriod(p || null);
                  }}
                  disabled={!periods.length}
                  className="border border-border rounded px-2 py-1.5 text-sm w-full disabled:bg-background-secondary disabled:text-text-muted"
                  data-testid="movements-period-select">
                  {!periods.length && <option value="">— No persisted findings for this granularity —</option>}
                  {periods.map(p => (
                    <option key={`${p.period_start}|${p.period_end}`}
                            value={`${p.period_start}|${p.period_end}`}>
                      {p.period_start} → {p.period_end} · {p.count} finding(s) · {fmtINR(p.recoverable_inr)}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
          {mode === 'custom' && (
            <>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">
                  <CalendarDays className="inline h-3 w-3 mr-1" /> From
                </label>
                <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)}
                       className="border border-border rounded px-2 py-1.5 text-sm"
                       data-testid="movements-start" />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wider text-text-secondary font-semibold mb-1">To</label>
                <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)}
                       className="border border-border rounded px-2 py-1.5 text-sm"
                       data-testid="movements-end" />
              </div>
            </>
          )}
          <Button onClick={load} disabled={loading || !effectiveStart} variant="outline" size="sm" data-testid="movements-reload">
            <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} /> Reload
          </Button>
          <div className="ml-auto flex gap-2">
            {!isClient && (
              <Button onClick={runEngine} disabled={running} variant="outline" size="sm"
                      className="border-primary text-primary" data-testid="movements-run-engine">
                {running ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Running…</>
                  : <><Sparkles className="h-3.5 w-3.5 mr-1" /> Run engine now</>}
              </Button>
            )}
            {!isClient && (
              <Button onClick={polishNarratives} disabled={polishing || !data.count}
                      variant="outline" size="sm"
                      className="border-sky-600 text-sky-700"
                      title="Rewrite narratives using Haiku (Emergent key). Never changes numbers."
                      data-testid="movements-polish">
                {polishing ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Polishing…</>
                  : <><Wand2 className="h-3.5 w-3.5 mr-1" /> Polish narratives</>}
              </Button>
            )}
            <Button onClick={downloadBrief} disabled={downloading || !data.count}
                    className="bg-primary hover:bg-primary-hover" size="sm"
                    data-testid="movements-download-brief">
              {downloading ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Generating…</>
                : <><Download className="h-3.5 w-3.5 mr-1" /> Download Brief</>}
            </Button>
            <Button onClick={downloadDisputeBrief} disabled={downloadingDispute || !data.count}
                    variant="outline" size="sm"
                    title="Counterparty-facing PDF · groups every visible finding by aggregator / acquirer / supplier"
                    className="border-primary text-primary hover:bg-primary/10"
                    data-testid="movements-download-dispute-brief">
              {downloadingDispute ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Compiling…</>
                : <><Download className="h-3.5 w-3.5 mr-1" /> Dispute Brief</>}
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-rose-50 border border-rose-200 rounded-md px-4 py-2 text-sm text-rose-800 flex items-center gap-2" data-testid="movements-error">
          <AlertTriangle className="h-4 w-4" /> {error}
        </div>
      )}

      {runSummary && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-md px-4 py-2 text-sm text-emerald-900 flex items-center gap-2" data-testid="movements-run-summary">
          <CheckCircle2 className="h-4 w-4" />
          Engine ran in {runSummary.duration_sec}s · {runSummary.findings_total} findings ·
          <strong className="ml-1">{fmtINR(runSummary.recoverable_total_inr)} recoverable</strong>
          <span className="text-emerald-700 ml-2 font-mono text-[11px]">{runSummary.engine_build_tag}</span>
        </div>
      )}

      {polishSummary && (
        <div className="bg-sky-50 border border-sky-200 rounded-md px-4 py-2 text-sm text-sky-900 flex items-center gap-2 flex-wrap" data-testid="movements-polish-summary">
          <Wand2 className="h-4 w-4" />
          Polished <strong>{polishSummary.polished}</strong> narrative(s).
          {polishSummary.rejected_hallucination > 0 && (
            <span className="text-rose-700 ml-2">
              {polishSummary.rejected_hallucination} rejected (hallucinated numbers → kept deterministic).
            </span>
          )}
          {polishSummary.failed > 0 && <span className="text-amber-700 ml-2">{polishSummary.failed} failed.</span>}
          <span className="text-sky-700 ml-2 text-[11px]">Numbers untouched — only prose changed.</span>
        </div>
      )}

      {/* KPI summary */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <Kpi label="Total Recoverable" value={fmtINR(data.total_recoverable_inr)}
             sub={`${(data.findings || []).filter(f => f.rule_id !== 'sup_mandi_premium_shift_v1').length} finding(s)`}
             tone="text-primary" data-testid="kpi-recoverable" />
        <Kpi label="Negotiation Headroom" value={fmtINR(data.negotiation_headroom_inr || 0)}
             sub="Mandi premium · not a claim"
             tone="text-amber-700" data-testid="kpi-headroom" />
        <Kpi label="High Priority" value={data.by_severity?.high ?? 0}
             sub="Immediate dispute action" tone="text-rose-700" />
        <Kpi label="Medium Priority" value={data.by_severity?.med ?? 0}
             sub="Verify & queue" tone="text-amber-700" />
        <Kpi label="Low Priority" value={data.by_severity?.low ?? 0}
             sub="Monitor" tone="text-slate-600" />
      </div>

      {/* Findings list */}
      <div className="bg-white border border-border rounded-md">
        <div className="px-4 py-2 bg-background-secondary text-[11px] font-semibold uppercase tracking-wider text-text-secondary">
          Findings ({data.count})
        </div>
        {loading && (
          <div className="p-8 text-center text-text-muted"><Loader2 className="inline h-4 w-4 animate-spin mr-2" /> Loading…</div>
        )}
        {!loading && data.count === 0 && (
          <div className="p-8 text-center text-text-muted" data-testid="movements-empty">
            {mode === 'custom'
              ? <>No findings in {customStart} → {customEnd}. Try widening the range or switch to <em>Tiles (curated)</em> for a guaranteed hit.</>
              : selectedPeriod
                ? <>No findings persisted for this period ({selectedPeriod.period_start} → {selectedPeriod.period_end}). Try <em>Run engine now</em>.</>
                : <>No periods available at this granularity yet. Click <em>Run engine now</em> to compute tiles.</>}
          </div>
        )}
        <ul className="divide-y divide-border">
          {data.findings.map((f) => {
            const sev = SEVERITY_STYLE[f.severity] || SEVERITY_STYLE.low;
            const SevIcon = sev.icon;
            return (
              <li key={f.id || f.natural_key} className="hover:bg-background-secondary cursor-pointer"
                  onClick={() => openDrawer(f)}
                  data-testid={`movements-finding-${f.natural_key}`}>
                <div className="px-4 py-3 flex items-center gap-4">
                  <span className={`h-2.5 w-2.5 rounded-full ${sev.dot} flex-shrink-0`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-text-primary truncate">{f.title}</h3>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded border ${sev.pill}`}>
                        <SevIcon className="inline h-3 w-3 mr-0.5" />
                        {f.severity}
                      </span>
                    </div>
                    <div className="text-[11px] text-text-muted mt-0.5">
                      {f.rule_id ? (
                        <>
                          {RULE_DOMAIN[f.rule_id] || f.rule_id} · confidence {Math.round((f.confidence || 0) * 100)}% ·
                          cites {f.citations?.length || 0} row(s)
                        </>
                      ) : (
                        <>{f.period_start} → {f.period_end} · cites {f.citations?.length || 0} row(s)</>
                      )}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-mono font-semibold text-text-primary">{fmtINR(f.amount)}</div>
                    <div className="text-[10px] text-text-muted">
                      {f.rule_id === 'sup_mandi_premium_shift_v1' ? 'headroom' : 'recoverable'}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={(e) => downloadEvidenceBundle(f, e)}
                    disabled={downloadingBundleId === f.id}
                    title="Download counterparty-facing dispute PDF"
                    aria-label="Download evidence bundle"
                    data-testid={`evidence-bundle-download-${f.natural_key}`}
                    className="flex-shrink-0 h-8 w-8 inline-flex items-center justify-center rounded-md border border-border text-text-muted hover:text-primary hover:border-primary hover:bg-background-secondary transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {downloadingBundleId === f.id
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Download className="h-3.5 w-3.5" />}
                  </button>
                  <ChevronRight className="h-4 w-4 text-text-muted flex-shrink-0" />
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Trace-view drawer */}
      {drawer && (
        <TraceDrawer
          finding={drawer.finding}
          resolvedCitations={drawer.resolvedCitations}
          onClose={() => setDrawer(null)}
          onDownloadBundle={downloadEvidenceBundle}
          downloadingBundleId={downloadingBundleId}
        />
      )}

      {/* Iter 114 · Boundaries panel — what Movements is NOT */}
      <BoundariesPanel />
    </div>
  );
};

const Kpi = ({ label, value, sub, tone }) => (
  <div className="bg-white border border-border rounded-md p-4" data-testid={`kpi-${label.replace(/ /g, '-').toLowerCase()}`}>
    <div className="text-[10px] uppercase tracking-wider text-text-secondary font-semibold">{label}</div>
    <div className={`text-2xl font-bold mt-1 ${tone || 'text-text-primary'}`}>{value}</div>
    <div className="text-[11px] text-text-muted mt-1">{sub}</div>
  </div>
);

const TraceDrawer = ({ finding, resolvedCitations, onClose, onDownloadBundle, downloadingBundleId }) => {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const cv = finding.computed_values || {};
  const cvEntries = Object.entries(cv);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-end" onClick={onClose}
         data-testid="movements-trace-drawer">
      <div className="w-full max-w-2xl h-full bg-white shadow-2xl overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-border px-5 py-3 flex items-start justify-between gap-3 z-10">
          <div className="min-w-0">
            {finding.rule_id && (
              <div className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">
                {RULE_DOMAIN[finding.rule_id] || finding.rule_id} · v{finding.rule_version}
              </div>
            )}
            <h2 className="text-base font-semibold text-primary truncate">{finding.title}</h2>
            {finding.engine_build_tag && (
              <div className="text-[11px] text-text-muted mt-0.5 font-mono">
                engine: {finding.engine_build_tag}
              </div>
            )}
          </div>
          <button onClick={onClose} className="text-text-muted hover:text-text-primary flex-shrink-0"
                  data-testid="trace-drawer-close" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          <div className="flex items-center gap-3">
            <div className="text-3xl font-mono font-bold text-primary">{fmtINR(finding.amount)}</div>
            <span className={`text-[11px] px-2 py-0.5 rounded border ${SEVERITY_STYLE[finding.severity]?.pill || ''}`}>
              {finding.severity} · confidence {Math.round((finding.confidence || 0) * 100)}%
            </span>
          </div>

          <p className="text-sm text-text-primary leading-relaxed">
            {finding.polished_narrative || finding.narrative}
          </p>
          {finding.polished_narrative && (
            <div className="flex items-center gap-2 text-[10px]">
              <span className="bg-sky-100 text-sky-800 px-1.5 py-0.5 rounded font-semibold">AI-polished</span>
              <span className="text-text-muted">via {finding.polish_model || 'claude-haiku'} · numbers verified against computed_values</span>
            </div>
          )}

          <div className="pt-1">
            <Button
              type="button"
              size="sm"
              onClick={(e) => onDownloadBundle && onDownloadBundle(finding, e)}
              disabled={downloadingBundleId === finding.id}
              data-testid="trace-drawer-download-bundle"
              className="w-full sm:w-auto"
            >
              {downloadingBundleId === finding.id
                ? <><Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> Preparing evidence bundle…</>
                : <><Download className="h-3.5 w-3.5 mr-2" /> Download evidence bundle (PDF)</>}
            </Button>
            <p className="text-[10px] text-text-muted mt-1.5">
              Counterparty-facing dispute pack. No internal machinery is included — evidence rows are pulled verbatim from the citations below.
            </p>
          </div>

          <section>
            <h3 className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">
              Computed values ({cvEntries.length})
            </h3>
            <div className="border border-border rounded-md overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-background-secondary">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">Metric</th>
                    <th className="text-right px-3 py-1.5 font-semibold">Value</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Derivation</th>
                  </tr>
                </thead>
                <tbody>
                  {cvEntries.map(([name, entry]) => (
                    <tr key={name} className="border-t border-border">
                      <td className="px-3 py-1.5 font-mono text-text-primary">{name}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{
                        typeof entry?.value === 'number'
                          ? entry.value.toLocaleString('en-IN', { maximumFractionDigits: 4 })
                          : String(entry?.value ?? '—')
                      }</td>
                      <td className="px-3 py-1.5 text-text-muted">{entry?.derived_from || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-xs uppercase tracking-wider text-text-secondary font-semibold mb-2">
              Source rows resolved
              <span className="text-text-muted font-normal ml-2">
                (showing first {Math.min(6, finding.citations?.length || 0)} of {finding.citations?.length || 0})
              </span>
            </h3>
            {!resolvedCitations && (
              <div className="text-xs text-text-muted"><Loader2 className="inline h-3 w-3 animate-spin mr-1" /> Resolving…</div>
            )}
            {resolvedCitations && (
              <div className="space-y-2">
                {resolvedCitations.map((c, i) => (
                  <div key={i} className="border border-border rounded-md p-3 bg-background-secondary/60"
                       data-testid={`trace-citation-${i}`}>
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <div className="font-mono text-[11px] text-text-primary">
                        <strong>{c.collection}</strong> / {c.id}
                      </div>
                      {c.ok
                        ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                        : <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />}
                    </div>
                    {c.ok ? (
                      <table className="w-full text-[11px]">
                        <tbody>
                          {Object.entries(c.row || {}).filter(([k]) => k !== 'id').map(([k, v]) => (
                            <tr key={k}>
                              <td className="text-text-muted pr-3 whitespace-nowrap align-top py-0.5">{k}</td>
                              <td className="font-mono text-text-primary py-0.5">
                                {typeof v === 'number'
                                  ? v.toLocaleString('en-IN', { maximumFractionDigits: 4 })
                                  : (v === '' ? <em className="text-text-muted">(empty)</em> : String(v))}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div className="text-xs text-rose-700">Resolve failed: {c.error}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="pt-2">
            <TrendingUp className="inline h-3 w-3 mr-1 text-text-muted" />
            {finding.rule_id ? (
              <span className="text-[11px] text-text-muted">
                Deterministic · rule <code>{finding.rule_id}</code> v{finding.rule_version} · natural key <code>{finding.natural_key}</code>
              </span>
            ) : (
              <span className="text-[11px] text-text-muted">
                Deterministic finding · {finding.period_start} → {finding.period_end}
              </span>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default Movements;
